import { useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import {
  ADMIN_BASE,
  ADMIN_LOGIN,
  ADMIN_ONBOARDING,
  ADMIN_ORGS,
  ADMIN_MODEL_GOVERNANCE,
  ADMIN_MODEL_CATALOG_HUB,
  ADMIN_CAPABILITIES,
  ADMIN_SKILL_HUB,
} from "../lib/paths.js";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { authAtom, clearAuthAtom } from "../lib/auth-store.js";
import { cn } from "../lib/cn.js";
import { LanguageSwitcher } from "./LanguageSwitcher.js";

// 平台面壳（super/support 专用）；org-admin 走 /:orgCode 的 TenantLayout 壳（2026-07-04 入口分离）
// label 是 i18n key（admin.nav.*），渲染时 t() 解析。
const NAV_ITEMS = [
  { to: ADMIN_BASE, labelKey: "admin.nav.overview" },
  { to: ADMIN_ORGS, labelKey: "admin.nav.organizations" },
  { to: ADMIN_ONBOARDING, labelKey: "admin.nav.onboarding" },
  { to: ADMIN_MODEL_CATALOG_HUB, labelKey: "admin.nav.modelCatalogHub" },
  { to: ADMIN_MODEL_GOVERNANCE, labelKey: "admin.nav.modelGovernance" },
  { to: ADMIN_CAPABILITIES, labelKey: "admin.nav.capabilities" },
  { to: ADMIN_SKILL_HUB, labelKey: "admin.nav.skillHub" },
];

export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const auth = useAtomValue(authAtom);
  const clearAuth = useSetAtom(clearAuthAtom);
  const navigate = useNavigate();

  const handleLogout = (): void => {
    clearAuth();
    navigate(ADMIN_LOGIN, { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-dosia-bg text-dosia-text">
      <aside className="w-56 shrink-0 border-r border-dosia-border bg-dosia-bg-light flex flex-col">
        <div className="px-4 py-4 border-b border-dosia-border">
          <p className="text-base font-semibold">{t("admin.shell.title")}</p>
          <p className="text-xs text-dosia-text-secondary mt-0.5">
            {t("admin.shell.subtitle")}
          </p>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === ADMIN_BASE}
              className={({ isActive }) =>
                cn(
                  "block rounded px-3 py-1.5 text-sm",
                  isActive
                    ? "bg-dosia-accent/15 text-dosia-text font-medium"
                    : "text-dosia-text-secondary hover:bg-dosia-bg-lighter",
                )
              }
            >
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 border-b border-dosia-border bg-dosia-bg-light flex items-center justify-end gap-3 px-4">
          <LanguageSwitcher />
          <span className="text-sm text-dosia-text-secondary">
            {auth?.scope === "super"
              ? t("admin.shell.role.super")
              : t("admin.shell.role.support")}
          </span>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-dosia-border px-2.5 py-1 text-xs text-dosia-text-secondary hover:bg-dosia-bg-lighter"
          >
            {t("admin.shell.logout")}
          </button>
        </header>
        <main className="flex-1 overflow-auto p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
